import neo4j, {Driver} from 'neo4j-driver';

import {BranchBuilder} from './model';
import {
    RelationsFilters,
    TaxpayerNode,
    TaxpayerNonNpd,
    TaxpayerRelationship,
    TaxpayerRelationshipType,
} from './types';

const filtersMap = (filters: RelationsFilters) =>
    Object.values({
        ...(filters.sur && {
            sur:
                'CASE child.surColour WHEN "RED" THEN 1 WHEN "YELLOW" THEN 2 WHEN "GREEN" THEN 3 ELSE 4 END',
        }),
        ...(filters.gap && {
            gap:
                'CASE child.transitOrGap WHEN "GAP_SOURCE" THEN 1 WHEN "TRANSIT_COMPANY" THEN 2 WHEN "OTHER" THEN 3 ELSE 4 END',
        }),
        ...(filters.sum && {
            sum: 'toFloat(rel.taxAmount) DESC',
        }),
        period: 'child.period',
    });

const QUERIES = {
    ROOT: (type: TaxpayerRelationshipType) =>
        `MATCH (root:TAXPAYER { tin: $tin, period: $period})${
            type === 'SALE' ? '' : '<'
        }-[r:SALE { taxPeriod: $period }]-${
            type === 'SALE' ? '>' : ''
        }(child:TAXPAYER { period: $period } WHERE child.tin <> $exclude AND child.name IS NOT NULL) return root, count(child) as childrenCount`,
    CHILDREN: (type: TaxpayerRelationshipType, filters: RelationsFilters, skip = 0, size = 5) =>
        `MATCH (parent:TAXPAYER { tin: $tin, period: $period})${
            type === 'SALE' ? '' : '<'
        }-[r1:SALE { taxPeriod: $period }]-${
            type === 'SALE' ? '>' : ''
        }(child:TAXPAYER { period: $period } WHERE child.tin <> $exclude AND child.name IS NOT NULL) OPTIONAL MATCH (child)${
            type === 'SALE' ? '' : '<'
        }-[r2:SALE]-${
            type === 'SALE' ? '>' : ''
        }(parent) WITH child, collect(r1) + collect(r2) as relationships UNWIND relationships as rel WITH child, rel ORDER BY ${filtersMap(
            filters,
        ).join(
            ', ',
        )} RETURN child as nodes, collect(rel) as relationships SKIP ${skip} LIMIT ${size}`,
    NON_NPD: (type: TaxpayerRelationshipType) =>
        `MATCH (n:TAXPAYER { tin: $tin, period: $period })${
            type === 'SALE' ? '' : '<'
        }-[r:SALE { taxPeriod: $period}]-${
            type === 'SALE' ? '>' : ''
        }(child:TAXPAYER { period: $period } WHERE child.tin <> $exclude AND child.name IS NULL) RETURN SUM(toFloat(r.taxAmount)) as taxAmount, SUM(toFloat(r.amountPrice)) as amountPrice, toFloat(count(child)) as count`,
};

interface BranchFetcherParams {
    type: TaxpayerRelationshipType;
    tin: string;
    period: number;
    size?: number;
    exclude?: string;
    filters: RelationsFilters;
    skip?: number;
}

class BranchFetcher {
    private uri = process.env.REACT_APP_NEO4J_URI as string;
    private user = process.env.REACT_APP_NEO4J_USER as string;
    private password = process.env.REACT_APP_NEO4J_PASSWORD as string;
    private driver?: Driver;
    private id = 1;

    async connect() {
        if (!this.uri || !this.user || !this.password)
            return new Error('Missing params when attempting to connect');
        if (this.driver) return;
        const driver = neo4j.driver(this.uri, neo4j.auth.basic(this.user, this.password), {
            encrypted: 'ENCRYPTION_ON',
        });
        await driver.verifyConnectivity();
        this.driver = driver;
    }

    async getBranch(params: BranchFetcherParams) {
        const response = await this.fetch(params);
        if (!response) return;

        return new BranchBuilder({
            root: response.parent,
            children: response.children.nodes,
            relationships: response.children.rels,
            nonNpd: response.nonNpd,
        });
    }

    async fetch(params: BranchFetcherParams) {
        await this.connect();

        const parent = await this.getRoot(params);
        const children = await this.getChildren(params);
        const nonNpd = await this.getNonNpd(params);

        if (!parent || !children || !nonNpd) return undefined;

        return {parent, children, nonNpd};
    }

    private async getRoot({type, tin, period, exclude = ''}: BranchFetcherParams) {
        if (!this.driver) return undefined;
        const response = await this.driver.executeQuery(QUERIES.ROOT(type), {tin, period, exclude});

        const root = response.records[0]?.get('root') as TaxpayerNode | undefined;
        if (!root) return undefined;
        root.uuid = this.id++;
        root.totalChildrenCount = response.records[0]?.get('childrenCount')?.low as number;
        return root;
    }

    private async getNonNpd({type, tin, period, exclude = ''}: BranchFetcherParams) {
        if (!this.driver) return undefined;
        const response = await this.driver.executeQuery(QUERIES.NON_NPD(type), {
            tin,
            period,
            exclude,
        });
        return {
            taxAmount: Number(response.records[0]?.get('taxAmount') || 0),
            amountPrice: Number(response.records[0]?.get('amountPrice') || 0),
            count: Number(response.records[0]?.get('count') || 0),
        } as TaxpayerNonNpd;
    }

    private async getChildren({
        type,
        tin,
        period,
        exclude = '',
        skip = 0,
        size = 5,
        filters,
    }: BranchFetcherParams) {
        if (!this.driver) return undefined;
        const response = await this.driver.executeQuery(
            QUERIES.CHILDREN(type, filters, skip, size),
            {tin, period, exclude},
            {database: 'neo4j'},
        );
        const nodes = response.records.map((record) => record.get('nodes')) as TaxpayerNode[];
        nodes.forEach((node) => {
            node.uuid = this.id++;
        });

        return {
            nodes,
            rels: (response.records.map((record) =>
                record.get('relationships'),
            ) as TaxpayerRelationship[][]).flat(),
        };
    }

    async close() {
        await this.driver?.close();
        this.driver = undefined;
    }
}

export const branchFetcher = new BranchFetcher();
